import { Dropdown } from '@statsbomb/kitbag-components';
import { useAtom, useAtomValue } from 'jotai';
import { Suspense, useEffect } from 'react';
import { useLocation, useMatch, useNavigate, useSearchParams } from 'react-router-dom';
import { trackGeneralUiInteractionEvent } from '@/analytics/snowplow/analytics';
import { radarTemplateIdAtom } from '@/atoms/radar';
import { METRIC_KEY, RADAR_TEMPLATE } from '@/consts/searchKeys';
import { SYSTEM_WIDE_USER_ID } from '@/consts/userConfigs';
import { useRadarTemplateOptions } from '@/hooks/useRadarTemplateOptions';
import { useUpdateQueryString } from '@/hooks/useUpdateQueryString';
import { PromiseAtom } from '@/types/atom';
import { StringOption } from '@/types/generic';
import { UserConfigRadarTemplate } from '@/types/userConfigs';

export const RadarTemplateDropdownBase = ({
  id = 'radar-templates-dropdown',
  label = '',
  isLoading = false,
  value,
  onChange = () => {},
  options,
}: {
  id?: string;
  label?: string;
  isLoading?: boolean;
  value?: StringOption | null;
  onChange?: (option: StringOption) => void;
  options?: { label: string; options: StringOption[] }[];
}) => (
  <Dropdown
    id={id}
    labelPosition="none"
    variant="primary"
    size="small"
    onChange={onChange}
    label={label}
    options={options}
    value={value}
    isLoading={isLoading}
  />
);

const RadarTemplateDropdownWithData = ({
  radarTemplateConfigsAtom,
  label = '',
}: {
  radarTemplateConfigsAtom: PromiseAtom<UserConfigRadarTemplate[]>;
  label?: string;
}) => {
  const radarTemplateConfigs = useAtomValue(radarTemplateConfigsAtom);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isTeamSection = !!useMatch('/:locale/team/*');
  const entity = isTeamSection ? 'team' : 'player';
  const isRadarPreview = !!useMatch('/:locale/:entity/:entityId/radar/preview');

  const options = useRadarTemplateOptions(radarTemplateConfigs);

  const radarTemplateParam = searchParams.get(RADAR_TEMPLATE);

  const [selectedRadarTemplateId, setSelectedRadarIdAtom] = useAtom(radarTemplateIdAtom);
  const selectedRadarTemplate = radarTemplateConfigs.find(template => template.configId === selectedRadarTemplateId);

  useEffect(() => {
    if (radarTemplateParam) {
      setSelectedRadarIdAtom(radarTemplateParam);
    }
  }, [radarTemplateParam]);

  const createQueryString = useUpdateQueryString(searchParams);

  const allOptions = options.flatMap(group => group.options);
  const value = selectedRadarTemplate
    ? allOptions.find(option => option.value === selectedRadarTemplate.configId)
    : null;

  const onChange = (option: StringOption) => {
    // Prevent unnecessary updates if the selected option is the same as the current one
    if (option.value === selectedRadarTemplateId) return;

    setSelectedRadarIdAtom(option.value);
    const path = isRadarPreview ? pathname.replace('/preview', '') : pathname;
    const queryParams = createQueryString([{ key: RADAR_TEMPLATE, value: String(option.value) }], [METRIC_KEY]);
    // close the radar preview panel if the radar preview outlet is open, otherwise stay on current path
    navigate(`${path}?${queryParams}`);

    const selectedTemplateConfig = radarTemplateConfigs.find(t => t.configId === option.value);
    const isCustomTemplate = !!selectedTemplateConfig && selectedTemplateConfig.hudlUserId !== SYSTEM_WIDE_USER_ID;

    trackGeneralUiInteractionEvent({
      interactionType: 'select',
      itemLocation: `${entity} radar`,
      itemName: `${isCustomTemplate ? 'custom' : 'statsbomb'}: ${option.label}`,
      itemType: 'dropdown',
    });
  };

  return <RadarTemplateDropdownBase label={label} options={options} value={value} onChange={onChange} />;
};

const RadarTemplateDropdown = ({
  radarTemplateConfigsAtom,
  label,
}: {
  radarTemplateConfigsAtom: PromiseAtom<UserConfigRadarTemplate[]>;
  label: string;
}) => (
  <Suspense fallback={<RadarTemplateDropdownBase label={label} isLoading />}>
    <RadarTemplateDropdownWithData radarTemplateConfigsAtom={radarTemplateConfigsAtom} label={label} />
  </Suspense>
);

export default RadarTemplateDropdown;
